import { useMemo } from 'react';

import { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { SESSION_CONTENT, SESSION_CONTENT_BOUNDED, type LogSessionContent } from '@/hooks/useLogs';
import { selectAccountId } from '@/redux/session/selectors';
import { LogSessionContentRow } from '@/shared/types/analytics';
import { computeSessionBoundDate } from '@/util/dateUtils';
import { isUuid } from '@/util/util';

/** Re-export the gql docs from `@/hooks/useLogs`, their single source. */
export { SESSION_CONTENT, SESSION_CONTENT_BOUNDED } from '@/hooks/useLogs';

export type UseSessionContentResult = {
  data?: LogSessionContentRow;
  isLoading: boolean;
  error?: ErrorLike;
  refetch: () => void;
};

/**
 * Apollo wrapper around the existing `SESSION_CONTENT` / `SESSION_CONTENT_BOUNDED`
 * queries. Does not dispatch to Redux. Apollo's normalized cache deduplicates calls,
 * so `usePrefetchSessionContent` warming the cache is enough, and every panel calling
 * this hook for the same `sessionId` shares one network round trip.
 */
export const useSessionContent = (
  sessionId?: string,
  sessionStart?: string
): UseSessionContentResult => {
  const accountId = useSelector(selectAccountId);

  const computedStartDate = useMemo(
    () => (sessionStart ? computeSessionBoundDate(sessionStart) : undefined),
    [sessionStart]
  );

  const { loading, data, error, refetch } = useQuery<LogSessionContent>(
    computedStartDate ? SESSION_CONTENT_BOUNDED : SESSION_CONTENT,
    {
      variables: computedStartDate ? { sessionId, startDate: computedStartDate } : { sessionId },
      skip: !sessionId || !accountId || !isUuid(sessionId),
    }
  );

  return { data: data?.rows?.[0], isLoading: loading, error, refetch };
};
